<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AgentConfigAPI from 'dashboard/api/agentConfig';

import IdentityForm from './components/IdentityForm.vue';
import PromptEditor from './components/PromptEditor.vue';
import ToolsList from './components/ToolsList.vue';
import CollectionFieldsBuilder from './components/CollectionFieldsBuilder.vue';
import QualificationLists from './components/QualificationLists.vue';
import OperationalConfig from './components/OperationalConfig.vue';
import QualificationRules from './components/QualificationRules.vue';
import PublishPanel from './components/PublishPanel.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const agentConfigId = computed(() => route.params.agentConfigId);
const isNew = computed(
  () => !agentConfigId.value || agentConfigId.value === 'new'
);

const activeTab = ref('identity');
const isLoading = ref(false);
const isSaving = ref(false);
const publishedJson = ref('');

const agentConfig = ref({
  nome_empresa: '',
  saudacao: '',
  nome_departamento: '',
  prompt_principal: '',
  status: 'draft',
  version: 1,
  tools: [],
  collection_fields: [],
  qualification_lists: [],
  operational_config: {
    hora_abertura: '08:00',
    hora_fechamento: '18:00',
    dias_funcionamento: ['seg', 'ter', 'qua', 'qui', 'sex'],
    numero_admin: '',
    grupo_whatsapp: '',
    modelo_ia: 'gpt-4o',
    label_qualificado: 'lead_qualificado',
    label_desqualificado: 'lead_desqualificado',
    holidays: [],
    team_mappings: [],
  },
  qualification_rules: [],
});

const tabs = [
  { id: 'identity', label: '1. Identidade', icon: 'i-lucide-user-check' },
  { id: 'prompt', label: '2. Prompt', icon: 'i-lucide-file-text' },
  { id: 'tools', label: '3. Tools', icon: 'i-lucide-wrench' },
  { id: 'fields', label: '4. Campos Coleta', icon: 'i-lucide-list-plus' },
  {
    id: 'lists',
    label: '5. Listas Qualificação',
    icon: 'i-lucide-list-checks',
  },
  { id: 'operational', label: '6. Operacional', icon: 'i-lucide-settings-2' },
  { id: 'rules', label: '7. Regras', icon: 'i-lucide-git-branch' },
  { id: 'publish', label: '8. Publicar', icon: 'i-lucide-upload-cloud' },
];

const fetchAgentConfig = async () => {
  if (isNew.value) return;
  try {
    isLoading.value = true;
    const response = await AgentConfigAPI.show(agentConfigId.value);
    const data = response.data.payload.agent_config;
    if (!data.operational_config) {
      data.operational_config = {
        hora_abertura: '08:00',
        hora_fechamento: '18:00',
        dias_funcionamento: ['seg', 'ter', 'qua', 'qui', 'sex'],
        holidays: [],
        team_mappings: [],
      };
    }
    agentConfig.value = data;
    await fetchPublishedJson();
  } catch (error) {
    useAlert('Erro ao carregar dados do agente.');
  } finally {
    isLoading.value = false;
  }
};

const fetchPublishedJson = async () => {
  if (isNew.value) return;
  try {
    const response = await AgentConfigAPI.getPublished(agentConfigId.value);
    publishedJson.value = JSON.stringify(response.data, null, 2);
  } catch (error) {
    // ignore
  }
};

const goBack = () => {
  router.push(accountScopedRoute('agent_configs_index'));
};

const saveConfig = async () => {
  if (!agentConfig.value.nome_empresa?.trim()) {
    useAlert('Por favor, informe o Nome da Empresa.');
    activeTab.value = 'identity';
    return;
  }

  try {
    isSaving.value = true;

    // Prepare nested attributes payload
    const payload = {
      nome_empresa: agentConfig.value.nome_empresa,
      saudacao: agentConfig.value.saudacao,
      nome_departamento: agentConfig.value.nome_departamento,
      prompt_principal: agentConfig.value.prompt_principal,
      tools_attributes: (agentConfig.value.tools || []).map((t, idx) => ({
        id: t.id,
        nome: t.nome,
        descricao: t.descricao,
        conteudo: t.conteudo,
        ordem: idx,
        _destroy: t._destroy,
      })),
      collection_fields_attributes: (
        agentConfig.value.collection_fields || []
      ).map((f, idx) => ({
        id: f.id,
        nome_interno: f.nome_interno,
        pergunta: f.pergunta,
        tipo: f.tipo,
        obrigatorio: f.obrigatorio,
        opcoes: f.opcoes,
        ordem: idx,
        _destroy: f._destroy,
      })),
      qualification_lists_attributes: (
        agentConfig.value.qualification_lists || []
      ).map(l => ({
        id: l.id,
        nome: l.nome,
        _destroy: l._destroy,
        items_attributes: (l.items || []).map(i => ({
          id: i.id,
          categoria: i.categoria,
          valor: i.valor,
          _destroy: i._destroy,
        })),
      })),
      operational_config_attributes: {
        id: agentConfig.value.operational_config?.id,
        hora_abertura: agentConfig.value.operational_config?.hora_abertura,
        hora_fechamento: agentConfig.value.operational_config?.hora_fechamento,
        dias_funcionamento:
          agentConfig.value.operational_config?.dias_funcionamento || [],
        numero_admin: agentConfig.value.operational_config?.numero_admin,
        grupo_whatsapp: agentConfig.value.operational_config?.grupo_whatsapp,
        modelo_ia: agentConfig.value.operational_config?.modelo_ia,
        label_qualificado:
          agentConfig.value.operational_config?.label_qualificado,
        label_desqualificado:
          agentConfig.value.operational_config?.label_desqualificado,
        holidays_attributes: (
          agentConfig.value.operational_config?.holidays || []
        ).map(h => ({
          id: h.id,
          data: h.data,
          descricao: h.descricao,
          _destroy: h._destroy,
        })),
        team_mappings_attributes: (
          agentConfig.value.operational_config?.team_mappings || []
        ).map(tm => ({
          id: tm.id,
          valor_campo: tm.valor_campo,
          chatwoot_team_id: tm.chatwoot_team_id,
          label_segmento: tm.label_segmento,
          _destroy: tm._destroy,
        })),
      },
      qualification_rules_attributes: (
        agentConfig.value.qualification_rules || []
      ).map(r => ({
        id: r.id,
        campo_avaliado_id: r.campo_avaliado_id,
        lista_id: r.lista_id,
        _destroy: r._destroy,
      })),
    };

    if (isNew.value) {
      const response = await AgentConfigAPI.create(payload);
      useAlert(t('AGENT_CONFIG.SAVED_SUCCESS'));
      const newId = response.data.payload.agent_config.id;
      router.replace(
        accountScopedRoute('agent_configs_edit', { agentConfigId: newId })
      );
    } else {
      await AgentConfigAPI.update(agentConfigId.value, payload);
      useAlert(t('AGENT_CONFIG.SAVED_SUCCESS'));
      await fetchAgentConfig();
    }
  } catch (error) {
    useAlert('Erro ao salvar configurações.');
  } finally {
    isSaving.value = false;
  }
};

const publishConfig = async () => {
  if (isNew.value) {
    await saveConfig();
  }
  try {
    isSaving.value = true;
    await AgentConfigAPI.publish(agentConfigId.value);
    useAlert(t('AGENT_CONFIG.PUBLISHED_SUCCESS'));
    await fetchAgentConfig();
  } catch (error) {
    useAlert('Erro ao publicar agente.');
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  fetchAgentConfig();
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <div class="flex flex-col gap-4 pb-4 border-b border-n-weak w-full">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <Button
              ghost
              slate
              sm
              icon="i-lucide-arrow-left"
              label="Voltar"
              @click="goBack"
            />
            <div class="h-4 w-px bg-n-weak" />
            <h1
              class="text-xl font-bold text-n-slate-12 flex items-center gap-2"
            >
              <span>{{
                agentConfig.nome_empresa || 'Novo Agente Isa Leal'
              }}</span>
              <span
                v-if="!isNew"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                :class="
                  agentConfig.status === 'published'
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-400'
                "
              >
                {{
                  agentConfig.status === 'published'
                    ? 'v' + agentConfig.version + ' Publicado'
                    : 'Rascunho'
                }}
              </span>
            </h1>
          </div>

          <div class="flex items-center gap-2">
            <Button
              ghost
              slate
              :is-loading="isSaving"
              icon="i-lucide-save"
              label="Salvar Alterações"
              @click="saveConfig"
            />
            <Button
              emerald
              :is-loading="isSaving"
              icon="i-lucide-upload-cloud"
              label="Publicar Versão"
              @click="publishConfig"
            />
          </div>
        </div>

        <!-- Abas de Navegação -->
        <div class="flex items-center gap-1 overflow-x-auto no-scrollbar pt-2">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer"
            :class="
              activeTab === tab.id
                ? 'bg-n-brand text-n-slate-1 shadow-sm'
                : 'bg-n-surface-1 text-n-slate-11 hover:bg-n-surface-2 hover:text-n-slate-12 border border-transparent'
            "
            @click="activeTab = tab.id"
          >
            <Icon :icon="tab.icon" class="size-3.5" />
            <span>{{ tab.label }}</span>
          </button>
        </div>
      </div>
    </template>

    <template #body>
      <div class="py-6">
        <IdentityForm v-if="activeTab === 'identity'" v-model="agentConfig" />

        <PromptEditor
          v-else-if="activeTab === 'prompt'"
          v-model="agentConfig.prompt_principal"
        />

        <ToolsList
          v-else-if="activeTab === 'tools'"
          v-model="agentConfig.tools"
        />

        <CollectionFieldsBuilder
          v-else-if="activeTab === 'fields'"
          v-model="agentConfig.collection_fields"
        />

        <QualificationLists
          v-else-if="activeTab === 'lists'"
          v-model="agentConfig.qualification_lists"
        />

        <OperationalConfig
          v-else-if="activeTab === 'operational'"
          v-model="agentConfig.operational_config"
        />

        <QualificationRules
          v-else-if="activeTab === 'rules'"
          v-model="agentConfig.qualification_rules"
          :collection-fields="agentConfig.collection_fields"
          :qualification-lists="agentConfig.qualification_lists"
        />

        <PublishPanel
          v-else-if="activeTab === 'publish'"
          :agent-config="agentConfig"
          :published-json="publishedJson"
          @publish="publishConfig"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
